import { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Divider,
  Group,
  Paper,
  Progress,
  Stack,
  Switch,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconInfoCircle,
  IconRefresh,
  IconSparkles,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api-client.ts";
import { getSpaces } from "@/features/space/services/space-service.ts";
import { ISpace } from "@/features/space/types/space.types";

// Shape matches the planned GET /ee/ai/ask/overview endpoint (Iter 2).
// While the endpoint is absent the component falls back to /spaces and
// renders zero stats — so the admin can already see and toggle Ask AI.
type AskAiSpaceRow = {
  space_id: string;
  space_slug: string;
  space_name: string;
  ai_enabled: boolean;
  pages_indexed: number;
  chunks_indexed: number;
  last_indexed_at?: string | null;
  questions_total?: number;
  questions_week?: number;
  thumbs_up?: number;
  thumbs_down?: number;
  cache_hit_rate?: number; // 0..1
  refusal_rate?: number; // 0..1
};

type AskAiOverview = {
  spaces: AskAiSpaceRow[];
  totals: {
    questions_total: number;
    questions_week: number;
    thumbs_up: number;
    thumbs_down: number;
    cache_hit_rate: number;
    refusal_rate: number;
    avg_latency_ms: number;
    cost_week_usd: number;
    by_lang: { lang: string; questions: number }[];
    by_model?: {
      model_version: string;
      questions: number;
      latency_p50_ms: number;
      cost_usd: number;
    }[];
  };
};

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleString() : "—";
const fmtPct = (v?: number) =>
  v == null ? "—" : `${Math.round(v * 100)}%`;
const fmtUsd = (v?: number) =>
  v == null ? "—" : `$${v.toFixed(2)}`;

export default function AskAiSpaces() {
  const { t } = useTranslation();
  const [data, setData] = useState<AskAiOverview | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string>("");

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get<AskAiOverview>("/ee/ai/ask/overview");
      setData(data);
    } catch {
      // Backend not yet wired (Iter 2). Build a zero-stats view from
      // the existing /spaces list so the admin already sees and can
      // pre-toggle Ask AI per space.
      try {
        const page = await getSpaces({ limit: 100 } as any);
        const items: ISpace[] = (page?.items as ISpace[]) || [];
        const rows: AskAiSpaceRow[] = items.map((s) => ({
          space_id: s.id,
          space_slug: s.slug,
          space_name: s.name,
          ai_enabled: false,
          pages_indexed: 0,
          chunks_indexed: 0,
          last_indexed_at: null,
          questions_total: 0,
          questions_week: 0,
          thumbs_up: 0,
          thumbs_down: 0,
          cache_hit_rate: 0,
          refusal_rate: 0,
        }));
        setData({
          spaces: rows,
          totals: {
            questions_total: 0,
            questions_week: 0,
            thumbs_up: 0,
            thumbs_down: 0,
            cache_hit_rate: 0,
            refusal_rate: 0,
            avg_latency_ms: 0,
            cost_week_usd: 0,
            by_lang: [],
          },
        });
      } catch (e: any) {
        setErr(e?.response?.data?.message || String(e));
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reindex = async (slug: string) => {
    setBusy((b) => ({ ...b, [slug]: true }));
    try {
      const { data: r } = await api.post<{
        pages: number;
        chunks_inserted: number;
        chunks_deleted: number;
        cache_invalidated: number;
      }>(`/ee/ai/ask/spaces/${slug}/reindex`);
      notifications.show({
        message: `Переиндексировано: ${r.pages} стр, +${r.chunks_inserted} / −${r.chunks_deleted} чанков, кэш инвалидирован ${r.cache_invalidated}`,
        color: "green",
      });
      await load();
    } catch (e: any) {
      notifications.show({
        color: "red",
        message:
          e?.response?.data?.message ||
          "Ошибка индексации (проверьте OPENAI_API_KEY на stage).",
      });
    } finally {
      setBusy((b) => ({ ...b, [slug]: false }));
    }
  };

  const toggle = async (slug: string, next: boolean) => {
    setBusy((b) => ({ ...b, [slug]: true }));
    try {
      await api.post(`/ee/ai/ask/spaces/${slug}/toggle`, { enabled: next });
      setData((d) =>
        d
          ? {
              ...d,
              spaces: d.spaces.map((r) =>
                r.space_slug === slug ? { ...r, ai_enabled: next } : r,
              ),
            }
          : d,
      );
    } catch (e: any) {
      notifications.show({
        color: "red",
        message:
          e?.response?.data?.message ||
          // Until Iter 2 lands the toggle persists locally only — make
          // that explicit so admins do not think changes are saved.
          "Бэкенд Ask AI ещё не готов: переключатель не сохраняется.",
      });
    } finally {
      setBusy((b) => ({ ...b, [slug]: false }));
    }
  };

  const totals = data?.totals;
  const totalVotes = (totals?.thumbs_up || 0) + (totals?.thumbs_down || 0);
  const upRate = totalVotes
    ? (totals!.thumbs_up || 0) / totalVotes
    : undefined;

  return (
    <Stack gap="md">
      <Alert
        color="gray"
        variant="light"
        icon={<IconInfoCircle size={16} />}
        title="Ask AI (stage)"
      >
        Ассистент по документации работает только на{" "}
        <Text component="span" fw={600}>
          stage.commitdoc.com
        </Text>
        . Включите Ask AI для нужных space — UI Fumadocs Ask AI появится
        на публичных страницах этих space. Источник ответов — только
        опубликованный контент Fumadocs, OpenAPI-спека не используется.
      </Alert>

      {err && (
        <Text size="sm" c="red">
          {err}
        </Text>
      )}

      <Divider label="Сводка (за 7 дней)" />
      <Paper withBorder p="md" radius="md">
        <Group gap="xl" wrap="wrap">
          <StatCell label="Вопросов" value={totals?.questions_week ?? 0} />
          <StatCell
            label="👍 / 👎"
            value={`${totals?.thumbs_up ?? 0} / ${totals?.thumbs_down ?? 0}`}
            hint={upRate != null ? `доля 👍 ${fmtPct(upRate)}` : undefined}
          />
          <StatCell
            label="Cache-hit"
            value={fmtPct(totals?.cache_hit_rate)}
            hint="ответы без вызова LLM"
          />
          <StatCell
            label="Отказы «не знаю»"
            value={fmtPct(totals?.refusal_rate)}
            hint="ниже порога релевантности"
          />
          <StatCell
            label="p50 латентность"
            value={
              totals?.avg_latency_ms
                ? `${Math.round(totals.avg_latency_ms)} мс`
                : "—"
            }
          />
          <StatCell
            label="Стоимость"
            value={fmtUsd(totals?.cost_week_usd)}
            hint="за 7 дней"
          />
          <StatCell
            label="Всего вопросов"
            value={totals?.questions_total ?? 0}
            hint="с начала сбора"
          />
        </Group>
        {totals?.by_lang?.length ? (
          <Stack gap={4} mt="md">
            <Text size="xs" c="dimmed">
              Разбивка по языкам
            </Text>
            <Group gap="xs" wrap="wrap">
              {totals.by_lang.map((l) => (
                <Badge key={l.lang} variant="light" color="gray">
                  {l.lang}: {l.questions}
                </Badge>
              ))}
            </Group>
          </Stack>
        ) : null}
        {totals?.by_model?.length ? (
          <Stack gap={4} mt="md">
            <Text size="xs" c="dimmed">
              Разбивка по моделям (A/B)
            </Text>
            <Table withTableBorder withColumnBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Версия</Table.Th>
                  <Table.Th>Вопросов</Table.Th>
                  <Table.Th>p50 латентность</Table.Th>
                  <Table.Th>Стоимость</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {totals.by_model.map((m) => (
                  <Table.Tr key={m.model_version}>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {m.model_version}
                      </Text>
                    </Table.Td>
                    <Table.Td>{m.questions}</Table.Td>
                    <Table.Td>{m.latency_p50_ms} мс</Table.Td>
                    <Table.Td>{fmtUsd(m.cost_usd)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        ) : null}
      </Paper>

      <Divider label="Spaces" />
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Space</Table.Th>
            <Table.Th>Ask AI</Table.Th>
            <Table.Th>Индекс</Table.Th>
            <Table.Th>Вопросов / нед</Table.Th>
            <Table.Th>👍 / 👎</Table.Th>
            <Table.Th>Cache-hit</Table.Th>
            <Table.Th>Последняя индексация</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {!data?.spaces?.length && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text size="sm" c="dimmed">
                  Нет space.
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {data?.spaces?.map((r) => {
            const total = (r.thumbs_up || 0) + (r.thumbs_down || 0);
            return (
              <Table.Tr key={r.space_id}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <IconSparkles
                      size={14}
                      opacity={r.ai_enabled ? 1 : 0.3}
                    />
                    <Text size="sm" fw={500}>
                      {r.space_name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      /{r.space_slug}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Tooltip
                    label={
                      r.chunks_indexed === 0 && !r.ai_enabled
                        ? "При включении запустится индексация контента"
                        : ""
                    }
                    disabled={r.chunks_indexed !== 0 || r.ai_enabled}
                  >
                    <Switch
                      checked={r.ai_enabled}
                      disabled={!!busy[r.space_slug]}
                      onChange={(e) =>
                        toggle(r.space_slug, e.currentTarget.checked)
                      }
                      aria-label={`Toggle Ask AI for ${r.space_slug}`}
                    />
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Text size="sm">
                        {r.pages_indexed} стр · {r.chunks_indexed} чанков
                      </Text>
                      {r.ai_enabled && r.chunks_indexed === 0 && (
                        <Progress value={5} animated size="xs" />
                      )}
                    </Stack>
                    <Tooltip label="Переиндексировать">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        disabled={!!busy[r.space_slug]}
                        onClick={() => reindex(r.space_slug)}
                        aria-label="Reindex"
                      >
                        <IconRefresh size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
                <Table.Td>{r.questions_week ?? 0}</Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {r.thumbs_up ?? 0} / {r.thumbs_down ?? 0}
                    {total > 0 && (
                      <Text component="span" size="xs" c="dimmed" ml={6}>
                        ({fmtPct((r.thumbs_up || 0) / total)})
                      </Text>
                    )}
                  </Text>
                </Table.Td>
                <Table.Td>{fmtPct(r.cache_hit_rate)}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {fmtDate(r.last_indexed_at)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed">
        Цифры собираются по событиям /api/chat (вопрос → ответ → оценка) и
        обновляются с задержкой до 1 минуты. Stage-only до окончания
        обкатки.
      </Text>
    </Stack>
  );
}

function StatCell({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <Stack gap={0} miw={120}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="lg" fw={600}>
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Stack>
  );
}
